import { useCurrentAccount } from '@application/api';
import { ENV, environmentVariable } from '@application/env';
import { useConfirmModal, useModal, useRouter, useValidation } from '@application/utils';
import type { MtSocialProvidersLinkEmailFormValues } from '@presentation/components';
import {
  ConfirmationModal,
  MtSocialProviderType,
  MtSocialProviders,
  MtSocialProvidersLinkEmailModal,
} from '@presentation/components';
import type { TokenResponse } from '@react-oauth/google';
import { useGoogleLogin } from '@react-oauth/google';
import { useCallback, useMemo } from 'react';
import type { ReactFacebookLoginProps } from 'react-facebook-login';
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props';
import type { SchemaOf } from 'yup';
import { object, ref, string } from 'yup';
import { useAccountProviders } from './api/useAccountProviders';
import { useLinkAccount } from './api/useLinkAccount';
import { useUnlinkAccount } from './api/useUnlinkAccount';

export const MtSocialProvidersController = () => {
  const { translate } = useRouter();
  const { emailMessage, minCharactersMessage, passwordDifferentMessage, requiredMessage } =
    useValidation();

  const { modal: confirmModal, confirmedShow, confirmHandler } = useConfirmModal();
  const { modal, show, close } = useModal();

  const { providersTypes, providersLoading } = useAccountProviders();
  const { linkWithFacebookToken, linkWithGoogleToken, linkAccountLoading, linkWithEmail } =
    useLinkAccount();
  const { unlinkAccount, unlinkAccountLoading } = useUnlinkAccount();
  const { currentAccountAuthType } = useCurrentAccount();

  const validationSchema = useMemo(() => {
    const schema: SchemaOf<MtSocialProvidersLinkEmailFormValues> = object({
      email: string().email(emailMessage).required(requiredMessage),
      password: string().min(6, minCharactersMessage(6)).required(requiredMessage),
      confirmPassword: string()
        .oneOf([ref('password'), null], passwordDifferentMessage)
        .required(requiredMessage),
    }).defined();
    return schema;
  }, [emailMessage, minCharactersMessage, passwordDifferentMessage, requiredMessage]);

  const googleAuthHandler = useCallback(
    (response: TokenResponse) => {
      const accessToken = response.access_token;
      linkWithGoogleToken(accessToken);
    },
    [linkWithGoogleToken],
  );

  const facebookAuthHandler: ReactFacebookLoginProps['callback'] = useCallback(
    (response) => {
      const accessToken = (response as any).accessToken as string | undefined;
      linkWithFacebookToken(accessToken);
    },
    [linkWithFacebookToken],
  );

  const signIn = useGoogleLogin({
    onSuccess: googleAuthHandler,
  });

  const typeClickHandler = useCallback(
    async (type: MtSocialProviderType, onFacebookClick) => {
      if (providersTypes.includes(type)) {
        // unlink
        const confirmed = await confirmedShow();
        if (!confirmed) return;
        return unlinkAccount(type);
      }
      //link

      if (type === MtSocialProviderType.FACEBOOK) {
        return onFacebookClick();
      }
      if (type === MtSocialProviderType.GOOGLE) {
        return signIn();
      }
      if (type === MtSocialProviderType.FIREBASE) {
        return show();
      }
    },
    [confirmedShow, providersTypes, show, signIn, unlinkAccount],
  );

  const emailFormSubmitHandler = useCallback(
    async (values: MtSocialProvidersLinkEmailFormValues) => {
      await linkWithEmail(values);
      close();
    },
    [close, linkWithEmail],
  );

  return (
    <>
      <ConfirmationModal
        onConfirm={confirmHandler}
        modal={confirmModal}
        text={translate('common.modals.unlinkAccount.text')}
      />
      <MtSocialProvidersLinkEmailModal
        modal={modal}
        form={{
          initialValues: {
            email: '',
            password: '',
            confirmPassword: '',
          },
          validationSchema,
          onSubmit: emailFormSubmitHandler,
        }}
      />
      <FacebookLogin
        version="4.0"
        appId={environmentVariable(ENV.FACEBOOK_ID)}
        callback={facebookAuthHandler}
        render={(renderProps: ReactFacebookLoginProps) => (
          <MtSocialProviders
            loading={providersLoading || linkAccountLoading || unlinkAccountLoading}
            linkedTypes={providersTypes}
            currentType={currentAccountAuthType}
            onClick={(type) => typeClickHandler(type, renderProps.onClick)}
          />
        )}
      />
    </>
  );
};
